import React, {
  MutableRefObject,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import AnimatedDiv from "src/app/components/AnimatedDiv/AnimatedDiv";
import InsightsDashboardTable from "./components/InsightsDashboardTable/InsightsDashboardTable";
import {
  IDTop,
  InsightsDashboardTop,
} from "./components/InsightsDashboardTop/InsightsDashboardTop";
import { InsightsDashboardSummary } from "./components/InsightsDashboardSummary/InsightsDashboardSummary";
import InsightsDashboardToolbar from "./components/InsightsDashboardToolbar/InsightsDashboardToolbar";
import { useQuery } from "src/app/methods/useQuery";
import { IDGalleryPane, PostsData } from "./panes/IDGalleryPane/IDGalleryPane";
import { IDDateRange } from "./types";
import MemoizedLoader from "../Loader/Loader";
import { InsightsDashboardTabs } from "./components/InsightsDashboardTabs/InsightsDashboardTabs";
import { IDPublications } from "./components/IDPublications/IDPublications";
import IDCharts from "./components/IDCharts/IDCharts";
import { IDNoResults } from "./components/IDNoResults/IDNoResults";
import { SocialInsightsDashboardList } from "./components/SocialInsightsDashboardList/SocialInsightsDashboardList";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";
import {
  setIDDates,
  setIDPrinting,
  setSelectedDashboard,
  setSetup,
  setShowTrends,
} from "src/redux";
import {
  getFiltersFromSetup,
  hydrateInsightsDashboardDateRange,
  hydrateInsightsDashboardSetup,
} from "./utils";
import { useParams } from "react-router";
import IDHFormattedMessage from "../IDHFormattedMessage/IDHFormattedMessage";
import { showToast } from "src/app/methods/showToast";
import axios from "axios";
import { API_URLS } from "src/utils/API_URLS";
import { AppConfig } from "src/config/AppConfig";
import { InsightsDashboardFilters } from "src/redux/insights-dashboard/types";
import { IDH_BRAND_COLOR } from "src/constants/idhBrandColor";
import { ReactComponent as IdhLogo } from "src/images/idh-logo-color.svg";
import { SOCIAL_LABELS } from "src/types";

import "./InsightsDashboard.scss";
import { camelCaseToTitleCase } from "src/utils/methods";
import { waitForPendingHeicRecoveries } from "src/utils/heicRecovery";

export interface RowBase {
  sum_likes: number;
  sum_comments: number;
  sum_engagements: number;
  avg_likes: number;
  avg_comments: number;
  avg_engagements: number;
  posts_count: number;
  unique_influencers: number;
  avg_er: number;
  avg_sentiment_negative: number;
  avg_sentiment_neutral: number;
  avg_sentiment_positive: number;
  avg_sentiment_product_related: number;
  sentiment_presence_percent: number;
}

export interface TableRow extends RowBase {
  key: string;
  sum_followers: number;
  avg_followers: number;
  meta: {
    icon: string | null;
    key: string;
    subtitle: string;
    title: string;
  };
}

export interface TotalRow extends RowBase {}

export type BreakdownMap = Record<string, TableRow[]>;

export type InsightsDashboardTableData = {
  total: TotalRow | null;
  breakdowns: BreakdownMap;
  top: IDTop;
};

export type IDPostsData = {
  list: PostsData[];
  page: number;
  total: number;
  pageSize: number;
  totalPages: number;
};

export type IDChartData = {
  [key: string]: {
    [key: string]: number;
  };
};

export interface IDDateRangeOption {
  value: string;
  label: string;
}

type IDSectionKey = "summaryAndCharts" | "top" | "publications" | "table";

const DEFAULT_SECTION_ORDER: IDSectionKey[] = [
  "summaryAndCharts",
  "top",
  "publications",
  "table",
];

interface Props {
  sectionsOrder?: IDSectionKey[];
  getData: (
    filters: InsightsDashboardFilters,
    overrides?: {
      dateRange?: string | null;
      dateFrom?: string | null;
      dateTo?: string | null;
      hashtagsCriteria?: string;
      mentionsCriteria?: string;
      hashtagsMentionsCriteria?: string;
      workspacePublicationsType?: string;
    },
  ) => void;
  loading?: boolean;
  data: InsightsDashboardTableData | null;
  comparisonData?: InsightsDashboardTableData | null;
  chartData: IDChartData | null;
  dateFrom: string | null;
  dateTo: string | null;
  dateRanges?: IDDateRange[];
  selectedDateRange: IDDateRangeOption | null;
  pageHeader?: ReactNode;
  blockDebounce: MutableRefObject<boolean>;
  skipSetupFetch?: MutableRefObject<boolean>;
}

interface IDSectionRendererArgs {
  data: InsightsDashboardTableData;
  comparisonData?: InsightsDashboardTableData | null;
  chartData: IDChartData | null;
  selectedDateRange: IDDateRangeOption | null;
  printing: boolean;
  breakdownOptions: string[];
  selectedBreakdown: string;
  setSelectedBreakdown: (value: string) => void;
  rows: TableRow[];
  comparisonRows: TableRow[];
  maxRows: number;
}

const idSectionRenderers: Record<
  IDSectionKey,
  (args: IDSectionRendererArgs) => ReactNode
> = {
  publications: ({ data }) => (
    <IDPublications
      data={{
        date: data.top.postsByDate,
        er: data.top.postsByEr,
      }}
    />
  ),
  top: ({ data }) => <InsightsDashboardTop data={data.top} />,
  summaryAndCharts: ({
    data,
    comparisonData,
    chartData,
    selectedDateRange,
  }) => (
    <div className="insights-dashboard__summary-and-charts">
      <InsightsDashboardSummary
        data={data.total}
        comparisonData={comparisonData?.total}
        selectedDateRange={selectedDateRange}
      />

      {chartData && <IDCharts data={chartData} />}
    </div>
  ),
  table: ({
    data,
    comparisonData,
    selectedDateRange,
    printing,
    breakdownOptions,
    selectedBreakdown,
    setSelectedBreakdown,
    rows,
    comparisonRows,
    maxRows,
  }) =>
    printing ? (
      <>
        {Object.entries(data.breakdowns).map(([key, tableRows]) => (
          <React.Fragment key={key}>
            <span className="insights-dashboard__table-title">
              {camelCaseToTitleCase(key)}
            </span>
            <InsightsDashboardTable
              rows={tableRows}
              comparisonRows={comparisonData?.breakdowns[key]}
              selectedBreakdown={key}
              selectedDateRange={selectedDateRange}
              maxRows={tableRows.length}
            />
          </React.Fragment>
        ))}
      </>
    ) : (
      <>
        <InsightsDashboardTabs
          breakdownOptions={breakdownOptions}
          selectedBreakdown={selectedBreakdown}
          setSelectedBreakdown={setSelectedBreakdown}
        />

        <InsightsDashboardTable
          rows={rows}
          comparisonRows={comparisonRows}
          selectedBreakdown={selectedBreakdown}
          selectedDateRange={selectedDateRange}
          maxRows={maxRows}
        />
      </>
    ),
};

const InsightsDashboard = (props: Props) => {
  const {
    getData,
    loading,
    data = null,
    comparisonData,
    chartData,
    dateFrom,
    dateTo,
    dateRanges,
    selectedDateRange,
    pageHeader,
    blockDebounce,
    skipSetupFetch,
    sectionsOrder = DEFAULT_SECTION_ORDER,
  } = props;

  const [selectedBreakdown, setSelectedBreakdown] = useState("influencerSize");

  const dispatch = useDispatch();
  const query = useQuery();
  const { dashboardId } = useParams<{ dashboardId?: string }>();

  const pdfRef = useRef<HTMLDivElement>(null);

  const type = query.get("type");
  const value = query.get("value");

  const { activeWorkspaceUuid, identity } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const {
    dashboardList,
    dashboardListFirstLoadDone,
    selectedDashboard,
    setup,
    setupConfig,
    printing,
  } = useSelector((state: RootState) => state.insightsDashboardReducer);

  const showIDGallery = type && value && selectedDashboard;

  const breakdownOptions = useMemo(
    () => Object.keys(data?.breakdowns || {}),
    [data],
  );

  useEffect(() => {
    if (
      !dashboardList.length ||
      selectedDashboard ||
      !dashboardId ||
      !setupConfig
    )
      return;

    const dashboard = dashboardList.find((item) => item.uuid === dashboardId);
    if (dashboard) {
      blockDebounce.current = true;

      const hydratedSetup = hydrateInsightsDashboardSetup(
        dashboard.filters,
        setupConfig,
      );
      const hydratedDateRange = hydrateInsightsDashboardDateRange(
        dashboard.filters.dateRange,
        dateRanges || [],
      );

      const hydratedFilters = getFiltersFromSetup(hydratedSetup, setupConfig);
      const { dateFrom, dateTo } = dashboard.filters;
      if (skipSetupFetch) skipSetupFetch.current = true;
      dispatch(setIDDates({ dateFrom, dateTo, dateRange: hydratedDateRange }));
      // showTrends is a local-only UI preference now — always starts off,
      // regardless of what's stored for this dashboard.
      dispatch(setShowTrends(false));
      dispatch(setSelectedDashboard(dashboard));
      dispatch(setSetup(hydratedSetup));
      getData(hydratedFilters, {
        dateFrom,
        dateTo,
        dateRange: hydratedDateRange?.value,
        workspacePublicationsType: hydratedSetup.workspacePublicationsType,
        hashtagsCriteria: hydratedSetup.hashtagsCriteria?.value ?? "or",
        mentionsCriteria: hydratedSetup.mentionsCriteria?.value ?? "or",
        hashtagsMentionsCriteria:
          hydratedSetup.hashtagsMentionsCriteria?.value ?? "or",
      });
    }
  }, [dashboardList, setupConfig]);

  useEffect(() => {
    if (!dashboardId && selectedDashboard) {
      dispatch(setSelectedDashboard(null));
    }
  }, [dashboardId]);

  useEffect(() => {
    if (!breakdownOptions.includes(selectedBreakdown)) {
      setSelectedBreakdown(breakdownOptions[0]);
    }
  }, [breakdownOptions]);

  const renderNoDataScreen = () => {
    if (!dashboardListFirstLoadDone) return null;

    if (dashboardList.length > 0) {
      return <SocialInsightsDashboardList />;
    }

    return <IDNoResults variant="noDashboard" />;
  };

  const handleGeneratePdf = () => {
    dispatch(setIDPrinting(true));
    setTimeout(() => {
      downloadPdf();
    }, 1000);
  };

  const waitForImages = async (element: HTMLElement): Promise<void> => {
    const images = Array.from(
      element.querySelectorAll<HTMLImageElement>("img"),
    );
    const imageLoadTimeout = 5000;

    await Promise.all(
      images.map((img) => {
        if (img.complete) {
          return img.decode?.().catch(() => undefined) ?? Promise.resolve();
        }

        return new Promise<void>((resolve) => {
          const timeout = window.setTimeout(resolve, imageLoadTimeout);
          const finish = () => {
            window.clearTimeout(timeout);
            resolve();
          };

          img.addEventListener("load", finish, { once: true });
          img.addEventListener("error", finish, { once: true });
        });
      }),
    );
  };

  const downloadPdf = async (): Promise<void> => {
    const reportName = `${selectedDashboard?.name ?? "report"}.pdf`;

    try {
      dispatch(setIDPrinting(true));
      const sourceElement = pdfRef.current;

      if (!sourceElement) {
        throw new Error("PDF source element not found");
      }

      await waitForImages(sourceElement);

      // waitForImages only waits for the native load/error event - a HEIC recovery it triggered
      // (fetch + heic2any conversion) is still running asynchronously at that point. Without this,
      // the DOM gets cloned/serialized mid-conversion and the PDF embeds the still-broken src.
      // See https://app.asana.com/1/242052987772680/project/807220246535156/task/1217546080711977?focus=true
      await waitForPendingHeicRecoveries();

      const head = document.head;

      const headHtml = head.outerHTML.replace(
        "<head>",
        `<head><base href="${AppConfig.getApiHost()}" />`,
      );

      const reportTemplate = sourceElement.cloneNode(true) as HTMLElement;

      const images = Array.from(
        reportTemplate.querySelectorAll<HTMLImageElement>("img"),
      );

      // blob: URLs (e.g. from client-side HEIC->JPEG recovery, see heicRecovery.ts) only live in this
      // tab's memory - once serialized into the HTML string below, they're dead links for the mediaproc
      // service rendering the PDF in its own headless browser. Inline them as data URIs instead, so the
      // image travels with the HTML itself.
      // See https://app.asana.com/1/242052987772680/project/807220246535156/task/1217546080711977?focus=true
      await Promise.all(
        images.map(async (img) => {
          if (!img.src.startsWith("blob:")) {
            img.src = img.src.replace(":3003", "");

            return;
          }

          try {
            const blobResponse = await fetch(img.src);
            const blob = await blobResponse.blob();
            const dataUri = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });

            img.src = dataUri;
          } catch {
            // Leave the (dead) blob: src as-is - mediaproc will just fail to load this one image,
            // same as it would have before this inlining step existed.
          }
        }),
      );

      const pdfHeight = pdfRef.current.scrollHeight;

      reportTemplate.style.overflow = "hidden";
      reportTemplate.style.height = `${pdfHeight}px`;

      const html = `<!DOCTYPE html><html lang="en">${headHtml}<body>${reportTemplate.outerHTML}</body></html>`;

      const response = await axios.post<Blob>(
        API_URLS.printPdfFromHtml,
        {
          fileName: reportName,
          width: 1850,
          height: pdfHeight + 10,
          html,
          wsWorkspaceUuid: activeWorkspaceUuid,
        },
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = reportName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
      console.error("PDF generation error:", error);

      if (axios.isAxiosError(error)) {
        console.error("Response:", error.response);
      }

      showToast(
        "error",
        <IDHFormattedMessage id="ws_error" defaultMessage="Error" />,
        <IDHFormattedMessage
          id="ws_something_went_wrong"
          defaultMessage="Something went wrong."
        />,
      );
    } finally {
      dispatch(setIDPrinting(false));
    }
  };

  const rows = data?.breakdowns[selectedBreakdown] || [];
  const comparisonRows = comparisonData?.breakdowns[selectedBreakdown] || [];
  const maxRows = Object.values(data?.breakdowns ?? {}).reduce(
    (max, rows) => Math.max(max, rows.length),
    0,
  );

  const customLogo =
    identity?.wsWorkspace?.smallLogo || identity?.wsWorkspace?.logo;
  const brandColor =
    identity?.wsWorkspace?.customPrimaryColor || IDH_BRAND_COLOR;

  const selectedSetup = setup;
  const selectedDateRangeLabel = selectedDateRange?.label || "Custom";
  const selectedDateRangeValue = selectedDateRange?.value;

  const coverTimeLabel = [
    selectedDateRangeLabel,
    selectedDateRangeValue === "custom" && dateFrom && dateTo
      ? `${dateFrom} - ${dateTo}`
      : null,
  ]
    .filter(Boolean)
    .join(": ");

  const setupPlatforms = selectedSetup?.platforms ?? [];
  const setupCountries = selectedSetup?.countries ?? [];
  const setupHashtags = selectedSetup?.hashtags ?? [];
  const setupMentions = selectedSetup?.mentions ?? [];

  const coverPlatforms = setupPlatforms.map(
    (platform) => SOCIAL_LABELS[platform] || platform,
  );
  const coverCountries = setupCountries.map((country) => country.label);
  const coverHashtags = setupHashtags;
  const coverMentions = setupMentions;

  const hasAnyResults = (data?.total?.posts_count ?? 0) > 0;

  const coverFiltersRows = [
    {
      label: "Time",
      value: coverTimeLabel,
      visible: Boolean(coverTimeLabel),
    },
    {
      label: "Platforms",
      value: coverPlatforms.join(", "),
      visible: coverPlatforms.length > 0,
    },
    {
      label: "Countries",
      value: coverCountries.join(", "),
      visible: coverCountries.length > 0,
    },
    {
      label: "Hashtags",
      value: coverHashtags.map((value) => `#${value}`).join(", "),
      visible: coverHashtags.length > 0,
    },
    {
      label: "Mentions",
      value: coverMentions.map((value) => `@${value}`).join(", "),
      visible: coverMentions.length > 0,
    },
  ].filter((row) => row.visible);
  return (
    <>
      {printing && (
        <div className="insights-dashboard__printing-indicator">
          <MemoizedLoader />
        </div>
      )}

      <AnimatedDiv ref={pdfRef} className="insights-dashboard">
        {printing && selectedDashboard && (
          <section className="insights-dashboard__cover-page">
            <div
              className="insights-dashboard__cover-page-main"
              style={{ backgroundColor: brandColor }}
            >
              <div className="insights-dashboard__cover-page-title-wrapper">
                <h1 className="insights-dashboard__cover-page-title">
                  {selectedDashboard.name}
                </h1>
                <span className="insights-dashboard__cover-page-subtitle">
                  Social Insights Dashboard
                </span>
              </div>

              {coverFiltersRows.length > 0 && (
                <div className="insights-dashboard__cover-page-filters">
                  {coverFiltersRows.map((row) => (
                    <div
                      key={row.label}
                      className="insights-dashboard__cover-page-filters-row"
                    >
                      <span className="insights-dashboard__cover-page-filters-label">
                        {row.label}
                      </span>
                      <span className="insights-dashboard__cover-page-filters-value">
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {!printing && pageHeader}

        {selectedDashboard ? (
          <>
            {!printing && (
              <InsightsDashboardToolbar
                blockDebounce={blockDebounce}
                downloadPdf={handleGeneratePdf}
                canDownloadPdf={hasAnyResults}
              />
            )}

            {loading ? (
              <MemoizedLoader />
            ) : data ? (
              <>
                {hasAnyResults ? (
                  <>
                    {sectionsOrder.map((sectionKey) => (
                      <React.Fragment key={sectionKey}>
                        {idSectionRenderers[sectionKey]({
                          data,
                          comparisonData,
                          chartData,
                          selectedDateRange,
                          printing,
                          breakdownOptions,
                          selectedBreakdown,
                          setSelectedBreakdown,
                          rows,
                          comparisonRows,
                          maxRows,
                        })}
                      </React.Fragment>
                    ))}

                    {printing && (
                      <section className="insights-dashboard__closing-page">
                        <div className="insights-dashboard__closing-page-content">
                          <div className="insights-dashboard__closing-page-logo">
                            {customLogo ? (
                              <img src={customLogo} alt="logo" />
                            ) : (
                              <IdhLogo />
                            )}
                          </div>

                          <span className="insights-dashboard__closing-page-dashboard-name">
                            {selectedDashboard.name}
                          </span>
                          <span className="insights-dashboard__closing-page-thank-you">
                            <IDHFormattedMessage
                              id="ws_thank_you"
                              defaultMessage="Thank you"
                            />
                          </span>
                        </div>

                        <div
                          className="insights-dashboard__closing-page-footer"
                          style={{ backgroundColor: brandColor }}
                        />
                      </section>
                    )}
                  </>
                ) : (
                  <IDNoResults variant="setup" />
                )}
              </>
            ) : null}
          </>
        ) : (
          renderNoDataScreen()
        )}

        {showIDGallery && (
          <IDGalleryPane
            dateFrom={dateFrom}
            dateTo={dateTo}
            dateRange={selectedDateRange}
          />
        )}
      </AnimatedDiv>
    </>
  );
};

export default InsightsDashboard;
